import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React from 'react';
import isEqual from 'react-fast-compare';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {additionalEditorSectionStyles} from 'analysis-tool/ui/components/AdditionalEditorSection';
import {getFlaggedRowsFromDataFrame} from 'analysis-tool/ui/flag-rows/FlagRowUtil';
import {GetChartViewDataFrame_dataFrame_DataFrameNode as VisualizationStepEditorDataFrameNode} from 'analysis-tool/ui/main-view/__generated/GetChartViewDataFrame';
import {assert} from 'Asserts';
import {isColumnTypeNumerical, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import HelpButton from 'help/HelpButton';
import {getColumnOptionDatas} from 'insights/analytics-visualizations/configs/config-components/ConfigEditorUtils';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import {FONT_PROXIMA_11, FONT_PROXIMA_13} from 'styles/FontConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {
  convertParameterCustomizationsToRegressionConfigParameters,
  convertRegressionConfigParametersToParameterCustomizations,
} from 'ui-library/charting/statistics/regression/regression-config-modal/RegressionConfigModal';
import {PARAMETER_NAME_TO_READABLE} from 'ui-library/charting/statistics/regression/regression-config-modal/RegressionConfigModalConstants';
import {
  RegressionConfigModalParameterConstraint,
  RegressionConfigModalParameterCustomization,
} from 'ui-library/charting/statistics/regression/regression-config-modal/RegressionConfigModalTypes';
import RegressionParameterConstraintEditor from 'ui-library/charting/statistics/regression/regression-config-modal/RegressionParameterConstraintEditor';
import {
  RegressionParameterName,
  RegressionParameterConstraintType,
  RegressionParameterCustomization,
  RegressionConfigParameterConstraint,
} from 'ui-library/charting/statistics/regression/RegressionTypes';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import * as modals from 'util/Modals';
import {assertNever, MakeAllFieldsOptional} from 'util/Types';

interface EditState {
  isConstraintsCollapsed: boolean;
  parameterCustomizations: Array<RegressionConfigModalParameterCustomization> | null;
}

const editGlobalState = makeUseGlobalClientState<EditState>({
  isConstraintsCollapsed: true,
  parameterCustomizations: null,
});

/** @package */
export type DraftRegressionConfig = MakeAllFieldsOptional<
  PipelineStepConfig<PipelineStepType.REGRESSION, 0>['regressionConfig']
>;

export type RegressionModelType = PipelineStepConfig<
  PipelineStepType.REGRESSION,
  0
>['regressionConfig']['modelType'];
const REGRESSION_TYPES: Array<RegressionModelType> = ['LINEAR', 'QUADRATIC', 'LOGISTIC'];
export const REGRESSION_TYPE_TO_READABLE: {[key in RegressionModelType]: string} = {
  LINEAR: 'Linear',
  QUADRATIC: 'Quadratic',
  LOGISTIC: '4-parameter logistic',
};

/** @package */
export const LINEAR_WEIGHT_HELP_TEXT = 'the statsmodels implementation';
/** @package */
export const LOGISTIC_WEIGHT_HELP_TEXT = 'the lmfit implementation';

const INITIAL_PARAMETER_CUSTOMIZATIONS: Array<RegressionConfigModalParameterCustomization> = [
  {
    name: RegressionParameterName.MIN,
    constraint: {type: RegressionParameterConstraintType.NONE},
    initialValue: {userDefined: false},
  },
  {
    name: RegressionParameterName.MAX,
    constraint: {type: RegressionParameterConstraintType.NONE},
    initialValue: {userDefined: false},
  },
  {
    name: RegressionParameterName.IC50,
    constraint: {type: RegressionParameterConstraintType.NONE},
    initialValue: {userDefined: false},
  },
  {
    name: RegressionParameterName.HILL_SLOPE,
    constraint: {type: RegressionParameterConstraintType.NONE},
    initialValue: {userDefined: false},
  },
];

export async function runConfirmCloseRegressionModal(title: string): Promise<void> {
  await modals.confirm(
    title,
    'All changes made to the regression configuration will be lost. Click "Apply" in order to save and run the regression.',
    ButtonStyle.NEGATIVE,
    {enableRedesign: true}
  );
}

function getDefaultParameterCustomizations(
  regressionConfig: DraftRegressionConfig
): Array<RegressionConfigModalParameterCustomization> {
  return regressionConfig.modelType !== 'LOGISTIC' || regressionConfig.parameterCustomizations == null
    ? INITIAL_PARAMETER_CUSTOMIZATIONS
    : convertRegressionConfigParametersToParameterCustomizations(
        regressionConfig.parameterCustomizations as Array<RegressionParameterCustomization>
      );
}

function updateConfigParameterCustomizations(
  regressionConfig: DraftRegressionConfig,
  parameterCustomizations: Array<RegressionConfigModalParameterCustomization>
): DraftRegressionConfig {
  return {
    ...regressionConfig,
    ...(regressionConfig.modelType === 'LOGISTIC'
      ? {
          parameterCustomizations: convertParameterCustomizationsToRegressionConfigParameters(
            parameterCustomizations.map((parameterCustomization) =>
              isValidConstraint(parameterCustomization.constraint)
                ? parameterCustomization
                : {
                    ...parameterCustomization,
                    constraint: {type: RegressionParameterConstraintType.NONE},
                  }
            )
          ),
        }
      : {}),
  };
}

function isValidConstraint(constraint: RegressionConfigModalParameterConstraint): boolean {
  switch (constraint.type) {
    case RegressionParameterConstraintType.NONE:
      return true;
    case RegressionParameterConstraintType.EQUAL:
      return constraint.value.numericValue != null;
    case RegressionParameterConstraintType.LESS_THAN:
      return constraint.upperBound.numericValue != null;
    case RegressionParameterConstraintType.GREATER_THAN:
      return constraint.lowerBound.numericValue != null;
    case RegressionParameterConstraintType.BETWEEN:
      return constraint.lowerBound.numericValue != null && constraint.upperBound.numericValue != null;
    default:
      assert(false, 'Got unexpected constraint type on', {constraint: assertNever(constraint)});
  }
}

function stringifyConstraint(constraint: RegressionConfigParameterConstraint): string {
  switch (constraint.type) {
    case RegressionParameterConstraintType.EQUAL:
      return `= ${constraint.value}`;
    case RegressionParameterConstraintType.LESS_THAN:
      return `< ${constraint.upperBound}`;
    case RegressionParameterConstraintType.GREATER_THAN:
      return `> ${constraint.lowerBound}`;
    case RegressionParameterConstraintType.BETWEEN:
      return `${constraint.lowerBound}–${constraint.upperBound}`;
    default:
      assert(false, 'Got unexpected constraint type on', {constraint});
  }
}

function ConstraintEditor({
  parameterCustomizations,
  setParameterCustomizations,
}: {
  parameterCustomizations: Array<RegressionConfigModalParameterCustomization>;
  setParameterCustomizations: (newCustomizations: Array<RegressionConfigModalParameterCustomization>) => void;
}): JSX.Element | null {
  return (
    <div className={css(styles.constraintsSection)}>
      {parameterCustomizations.map(({name, constraint}) => (
        <div className={css(styles.constraintParameter)} key={name}>
          <RegressionParameterConstraintEditor
            parameterName={name}
            constraint={constraint}
            onChange={(newConstraint) => {
              const newParameterCustomizations = produce(parameterCustomizations, (draftParameters) => {
                draftParameters.forEach((parameter) => {
                  if (parameter.name === name) {
                    parameter.constraint = newConstraint;
                  }
                });
              });
              setParameterCustomizations(newParameterCustomizations);
            }}
            wrapperClassName={css(styles.wrapper)}
            labelClassName={css(styles.label)}
            typeSelectorClassName={css(styles.typeSelector)}
            inputsWrapperClassName={css(styles.numericInputsWrapper)}
            inputClassName={css(styles.numericInput)}
          />
        </div>
      ))}
    </div>
  );
}

interface ModelTypeDropdownProps {
  regressionConfig?: DraftRegressionConfig;
  onSelectModelType: (newRegressionModelType: RegressionModelType) => void;
  isDisabled: boolean;
}

function ModelTypeDropdown({
  regressionConfig,
  onSelectModelType,
  isDisabled,
}: ModelTypeDropdownProps): JSX.Element {
  const displayName =
    regressionConfig?.modelType != null
      ? REGRESSION_TYPE_TO_READABLE[regressionConfig?.modelType]
      : 'Select a regression type';

  return (
    <SelectDropdown
      displayName={displayName}
      menu={
        <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value="">
          {REGRESSION_TYPES.map((regressionTypeOption) => (
            <Option
              key={regressionTypeOption}
              onSelect={onSelectModelType}
              value={regressionTypeOption}
              dataTestAttrs={{
                component: 'ModelTypeDropdown',
                element: 'modelType',
                key: regressionTypeOption,
              }}
            >
              {REGRESSION_TYPE_TO_READABLE[regressionTypeOption]}
            </Option>
          ))}
        </Menu>
      }
      disabled={isDisabled}
      dataTestAttrs={{
        component: 'ModelTypeDropdown',
        element: 'button',
      }}
    />
  );
}

interface WeightColumnDropdownProps {
  regressionConfig?: DraftRegressionConfig;
  setRegressionConfig: (newRegressionConfig: DraftRegressionConfig) => void;
  inputDataFrame: VisualizationStepEditorDataFrameNode;
  isDisabled: boolean;
}

function WeightColumnDropdown({
  regressionConfig,
  setRegressionConfig,
  inputDataFrame,
  isDisabled,
}: WeightColumnDropdownProps): JSX.Element {
  const weightColumns = inputDataFrame.allColumnsJson.filter((column) =>
    isColumnTypeNumerical(column.type)
  ) as Array<DataFrameColumnDef>;

  return (
    <DropdownSelector
      allowNull={true}
      onChange={(selectedColumnDisplayName: string) => {
        const selectedColumnSystemName =
          selectedColumnDisplayName == null
            ? null
            : (weightColumns.find((column) => column.displayName === selectedColumnDisplayName)?.systemName ??
              null);
        setRegressionConfig({
          ...regressionConfig,
          weightColumn: selectedColumnSystemName,
        });
      }}
      searchable={true}
      selectedValue={
        weightColumns.find((column) => column.systemName === regressionConfig?.weightColumn)?.displayName ??
        null
      }
      optionDatas={getColumnOptionDatas(weightColumns, false)}
      disabled={isDisabled}
    />
  );
}

export interface RegressionEditorSectionProps {
  analysis: PipelineGraphQL;
  clientStateId: string;
  regressionStep: GetAnalysisPipelineStep;
  regressionConfig: DraftRegressionConfig;
  setRegressionConfig: (newRegressionConfig: DraftRegressionConfig) => void;
  inputDataFrame: VisualizationStepEditorDataFrameNode;
  runButtonIsDisabled: boolean;
  onRunRegression: (config: DraftRegressionConfig) => Promise<void>;
  onCancelRegression: () => void;
  isReadOnly: boolean;
  setIsReadOnly: (newIsReadOnly: boolean) => void;
}

export default function RegressionEditorSidebarSection({
  analysis,
  clientStateId,
  regressionStep,
  regressionConfig,
  setRegressionConfig,
  inputDataFrame,
  runButtonIsDisabled,
  onRunRegression,
  onCancelRegression,
  setIsReadOnly,
  isReadOnly,
}: RegressionEditorSectionProps): JSX.Element {
  const [editState, setEditState] = editGlobalState.useGlobalClientState(clientStateId);

  // Regression views have single-node lineages containing only the REGRESSION step,
  // so we walk the graph to find the parent step for checking flagged rows.
  const parentStep = getParentSteps(analysis, regressionStep)[0] ?? null;
  const flaggedRows =
    parentStep != null
      ? getFlaggedRowsFromDataFrame(analysis, parentStep, inputDataFrame.dataFrameJson.rows)
      : new Set<string>();
  const hasFlaggedRows = flaggedRows.size > 0;

  const persistedRegressionConfig:
    | PipelineStepConfig<PipelineStepType.REGRESSION, 0>['regressionConfig']
    | null =
    (regressionStep.config?.variables.find((variable) => variable.name === 'regressionConfig')
      ?.value as PipelineStepConfig<PipelineStepType.REGRESSION, 0>['regressionConfig']) ?? null;

  let parameterCustomizations: Array<RegressionParameterCustomization> = [];
  if (
    persistedRegressionConfig?.modelType === 'LOGISTIC' &&
    persistedRegressionConfig.parameterCustomizations != null
  ) {
    parameterCustomizations = persistedRegressionConfig.parameterCustomizations.filter(
      ({constraint}) => constraint.type !== RegressionParameterConstraintType.NONE
    ) as Array<RegressionParameterCustomization>;
  }

  function handleSetParameterCustomizations(
    newParameterCustomizations: Array<RegressionConfigModalParameterCustomization>
  ): void {
    setEditState({...editState, parameterCustomizations: newParameterCustomizations});
    setRegressionConfig(updateConfigParameterCustomizations(regressionConfig, newParameterCustomizations));
  }

  function onSelectModelType(newRegressionModelType: RegressionModelType): void {
    if (regressionConfig.modelType !== newRegressionModelType) {
      if (regressionConfig.modelType === 'LOGISTIC' && regressionConfig.parameterCustomizations != null) {
        // Remove any existing parameter customizations before switching away from a LOGISTIC regression type
        const {parameterCustomizations, ...sharedProps} = regressionConfig;
        setRegressionConfig({...sharedProps, modelType: newRegressionModelType});
      } else {
        setRegressionConfig(
          updateConfigParameterCustomizations(
            {...regressionConfig, modelType: newRegressionModelType},
            editState.parameterCustomizations ?? getDefaultParameterCustomizations(regressionConfig)
          )
        );
      }
    }
  }

  async function handleCancelRegression(): Promise<void> {
    const persistedRegressionConfig =
      regressionStep.config?.variables.find((variable) => variable.name === 'regressionConfig')?.value ??
      null;

    if (!isEqual(persistedRegressionConfig, regressionConfig)) {
      await runConfirmCloseRegressionModal('Cancel?');
    }

    // Reset parameter state
    setEditState({
      ...editState,
      parameterCustomizations:
        persistedRegressionConfig == null
          ? INITIAL_PARAMETER_CUSTOMIZATIONS
          : getDefaultParameterCustomizations(persistedRegressionConfig),
    });
    setIsReadOnly(true);

    onCancelRegression();
  }

  return (
    <div className={css(additionalEditorSectionStyles.wrapper)}>
      <div className={css(additionalEditorSectionStyles.header, additionalEditorSectionStyles.borderTop)}>
        Regression
      </div>
      <div className="u-small-spacing-top">
        <FieldLabel label="Regression model" />
        <ModelTypeDropdown
          regressionConfig={isReadOnly ? persistedRegressionConfig : regressionConfig}
          onSelectModelType={onSelectModelType}
          isDisabled={isReadOnly}
        />
      </div>
      {(!isReadOnly || persistedRegressionConfig?.weightColumn != null) && (
        <div className="u-small-spacing-top">
          <FieldLabel label="Weights" />
          <HelpButton
            renderContent={() => (
              <span className="u-pre-wrap u-normal-font-weight">
                Select weights to assign relative importance of each individual point in the regression, which
                can improve the accuracy of your model. See{' '}
                {regressionConfig.modelType === 'LOGISTIC' ? (
                  <a href="https://lmfit.github.io/lmfit-py/model.html#lmfit.model.Model.fit">
                    {LOGISTIC_WEIGHT_HELP_TEXT}
                  </a>
                ) : (
                  <a href="https://www.statsmodels.org/stable/generated/statsmodels.regression.linear_model.WLS.html">
                    {LINEAR_WEIGHT_HELP_TEXT}
                  </a>
                )}{' '}
                Leaving this selection blank results in ordinary least squares regression.
              </span>
            )}
            iconClassName="u-small-spacing-left"
            placement={FloatingPlacement.TOP}
            interaction={FloatingInteraction.HOVER}
          />
          <WeightColumnDropdown
            regressionConfig={isReadOnly ? persistedRegressionConfig : regressionConfig}
            setRegressionConfig={setRegressionConfig}
            inputDataFrame={inputDataFrame}
            isDisabled={isReadOnly}
          />
        </div>
      )}

      {hasFlaggedRows && (!isReadOnly || persistedRegressionConfig?.excludeFlagged) && (
        <div className="u-small-spacing-top">
          <CheckboxInput
            id="exclude-flagged-checkbox"
            label="Exclude flagged values"
            helpText="When checked, flagged rows will be excluded from the regression calculation but will still appear on the chart."
            checked={
              isReadOnly
                ? (persistedRegressionConfig?.excludeFlagged ?? false)
                : (regressionConfig.excludeFlagged ?? false)
            }
            onChange={(e) => {
              setRegressionConfig({...regressionConfig, excludeFlagged: e.target.checked});
            }}
            disabled={isReadOnly}
          />
        </div>
      )}

      {regressionConfig.modelType === 'LOGISTIC' &&
        (isReadOnly ? (
          parameterCustomizations.map((parameterCustomization) => (
            <div
              key={parameterCustomization.name}
              className={classNames(css(styles.wrapper), 'u-small-spacing-top')}
            >
              <FieldLabel label={PARAMETER_NAME_TO_READABLE[parameterCustomization.name]} />
              {stringifyConstraint(parameterCustomization.constraint)}
            </div>
          ))
        ) : (
          <div>
            <div
              key="constraints"
              onClick={() => {
                setEditState({...editState, isConstraintsCollapsed: !editState.isConstraintsCollapsed});
              }}
              className={classNames('u-small-spacing-top', css(styles.advancedSettingsHeader))}
            >
              <BenchlingIcon
                icon={editState.isConstraintsCollapsed ? 'caret-right' : 'caret-down'}
                className={css(styles.collapseIcon)}
              />
              Constraints
            </div>
            {!editState.isConstraintsCollapsed && (
              <ConstraintEditor
                parameterCustomizations={
                  editState.parameterCustomizations ?? getDefaultParameterCustomizations(regressionConfig)
                }
                setParameterCustomizations={handleSetParameterCustomizations}
              />
            )}
          </div>
        ))}

      {isReadOnly && <div className={css(styles.unsetWarning)}>Unset options not shown</div>}

      <div className={css(styles.buttonsSection)}>
        {isReadOnly ? (
          <Button
            key="edit"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            fullWidth={true}
            className={css(styles.editButton)}
            size={ButtonSize.MEDIUM}
            onClick={() => {
              setIsReadOnly(false);
            }}
            text="Edit"
            icon="edit"
          />
        ) : (
          <>
            <Button
              key="cancel"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              fullWidth={false}
              className={css(styles.button)}
              size={ButtonSize.MEDIUM}
              onClick={handleCancelRegression}
              text="Cancel"
            />
            <Tooltip
              disabled={!runButtonIsDisabled}
              title="Select a regression model type"
              placement={TooltipPlacement.LEFT}
            >
              <Button
                key="apply"
                buttonStyle={ButtonStyle.SECONDARY}
                fullWidth={false}
                className={css(styles.button)}
                size={ButtonSize.MEDIUM}
                disabled={runButtonIsDisabled}
                onClick={() => {
                  onRunRegression(regressionConfig);
                  setIsReadOnly(true);
                }}
                text="Apply"
                dataTestAttrs={{
                  component: 'RegressionEditorSidebarSection',
                  element: 'applyRegressionButton',
                }}
              />
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}

/** @package */
export const styles = StyleSheet.create({
  buttonsSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  editButton: {
    marginTop: 12,
  },
  button: {
    marginTop: 12,
    marginLeft: 8,
  },
  collapseIcon: {
    color: Colors.GRAY_DARKER,
    paddingLeft: 4,
    paddingRight: 8,
  },
  advancedSettingsHeader: {
    ...FONT_PROXIMA_11,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    color: Colors.GRAY_DARKER,
    paddingBottom: 8,
    cursor: 'pointer',
  },
  constraintsSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  constraintParameter: {
    marginBottom: 8,
  },
  unsetWarning: {
    ...FONT_PROXIMA_11,
    fontStyle: 'italic',
    paddingTop: 12,
  },
  // Constraint editor styles
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    ...FONT_PROXIMA_13,
    fontWeight: 'bold',
  },
  typeSelector: {
    width: '100%',
    marginBottom: 4,
  },
  numericInputsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 4,
  },
  numericInput: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: 4,
  },
});
